import { createGlobalStyle } from 'styled-components';

// Single dark theme, matching the "skeered.net - site redesign" canvas.
// Legacy token names are kept so Shop / Auth / Dashboard / Analytics keep working.
const GlobalStyle = createGlobalStyle`
  :root {
    /* design palette */
    --brand: #ff7a2e;
    --brand-ink: #0a0b0d;
    --live: #3ddc84;
    --text-code: #c9cdd4;

    --background: #0a0b0d;
    --foreground: #edeef0;
    --card: #111317;
    --card-foreground: #edeef0;
    --popover: #111317;
    --popover-foreground: #edeef0;
    --primary: #edeef0;
    --primary-foreground: #0a0b0d;
    --secondary: #15181d;
    --secondary-foreground: #edeef0;
    --muted: #15181d;
    --muted-foreground: #7c828e;
    --accent: #15181d;
    --accent-foreground: #edeef0;
    --destructive: #f05252;
    --destructive-foreground: #ffffff;
    --border: #23262d;
    --input: #23262d;
    --ring: #ff7a2e;
    --radius: 0.625rem;
    --chart-1: #ffb08a;
    --chart-2: #ff7a2e;
    --chart-3: #e05f14;
    --chart-4: #4f8cff;
    --chart-5: #3ddc84;
    --sidebar: #0d0f12;
    --sidebar-foreground: #edeef0;
    --sidebar-primary: #edeef0;
    --sidebar-primary-foreground: #0a0b0d;
    --sidebar-accent: #15181d;
    --sidebar-accent-foreground: #edeef0;
    --sidebar-border: #23262d;
    --sidebar-ring: #ff7a2e;
    --spacing: 0.25rem;

    --font-display: 'Bricolage Grotesque', system-ui, sans-serif;
    --font-sans: 'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif;
    --font-body: var(--font-sans);
    --font-mono: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    --font-serif: ui-serif, Georgia, serif;

    --surface-0: var(--background);
    --surface-1: #0d0f12;
    --surface-2: var(--card);
    --surface-3: #15181d;

    --primary-color: var(--surface-0);
    --secondary-color: var(--secondary);
    --secondary-light: var(--card);

    --text-color: var(--foreground);
    --text-primary: var(--foreground);
    --text-secondary: #a3a8b3;
    --text-tertiary: #7c828e;

    --accent-color: var(--primary);
    --accent-hover: #ffffff;
    --accent-muted: var(--muted-foreground);

    --border-subtle: #1e2127;
    --border-accent: #2e323a;

    --glass-bg: rgba(10, 11, 13, 0.92);
    --glass-border: #1e2127;
    --glass-highlight: none;

    --success: #3ddc84;
    --warning: #f5c542;
    --error: #f05252;
    --info: #4f8cff;

    --header-height: 4.5rem;
    --page-max-width: 75rem;

    --radius-sm: 0.25rem;
    --radius-md: 0.375rem;
    --radius-lg: 0.625rem;
    --radius-xl: 0.75rem;
    --radius-2xl: 1rem;
    --radius-full: 9999px;

    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.3);
    --shadow-md: 0 4px 10px rgba(0, 0, 0, 0.3);
    --shadow-lg: 0 8px 20px rgba(0, 0, 0, 0.35);
    --shadow-xl: 0 12px 28px rgba(0, 0, 0, 0.4);
    --shadow-2xl: 0 16px 36px rgba(0, 0, 0, 0.45);
    --shadow-glow: none;

    --transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-base: 220ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-slow: 320ms cubic-bezier(0.4, 0, 0.2, 1);

    color-scheme: dark;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }

    html {
      scroll-behavior: auto;
    }
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: var(--font-sans);
    font-size: 16px;
    background: var(--surface-0);
    color: var(--text-color);
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
    min-height: 100vh;
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-display);
    font-weight: 600;
    line-height: 1.15;
    color: var(--text-primary);
    letter-spacing: -0.02em;
  }

  h1 {
    font-size: clamp(2.5rem, 5.5vw, 4.25rem);
  }

  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
  }

  h3 {
    font-size: clamp(1.25rem, 2.5vw, 1.5rem);
  }

  p {
    color: var(--text-secondary);
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button,
  input,
  textarea,
  select {
    font: inherit;
  }

  button {
    cursor: pointer;
    border: none;
    outline: none;
  }

  img,
  video {
    max-width: 100%;
  }

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--surface-1);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--border-accent);
    border-radius: var(--radius-full);
  }

  ::selection {
    background: rgba(255, 122, 46, 0.3);
    color: var(--text-primary);
  }

  *:focus-visible {
    outline: 2px solid var(--brand);
    outline-offset: 2px;
  }
`;

export default GlobalStyle;
